import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { CartItemsComponent } from '@theme/component-cart-items';
import { beforeCartChange, drawingParts, partsDrawn, revealWhenDrawn } from '@theme/sl-cart-parts';

/**
 * Stillwell product form (sections/sl-product.liquid): add to cart without leaving the page, then open the cart drawer.
 *
 * Posts the native form to /cart/add.js with the cart sections, and dispatches the same CartLinesUpdateEvent as
 * Horizon's product form, so the drawer, its line items and the header count re-render from one response.
 * Without the drawer (cart type "page", or on /cart) or without JavaScript, the form posts natively to /cart/add.
 *
 * The drawer opens once, complete: the markup from the add response goes in while the drawer is still closed, its
 * images are decoded, the payment buttons and the installments line draw themselves (the closed drawer stays laid out
 * for that, see assets/sl-horizon.css; what "drawn" means: assets/sl-cart-parts.js), and only then does it slide in.
 * The scripts those parts need start loading on the tap, alongside /cart/add.js. /cart.json (needed for the cart
 * event) loads alongside the drawer instead of in front of it.
 */

/** @returns {import('@theme/theme-drawer').ThemeDrawer | null} */
function cartDrawer() {
  return /** @type {any} */ (document.getElementById('cart-drawer'));
}

async function openDrawer() {
  await customElements.whenDefined('theme-drawer');
  const drawer = cartDrawer();
  if (drawer && !drawer.isOpen) drawer.open();
}

/** @returns {Promise<any>} */
async function fetchCart() {
  /** @type {any} */
  const items = document.querySelector('cart-items-component');
  if (items) {
    await customElements.whenDefined('cart-items-component');
    return items.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Waits until the images inside `root` are loaded and decoded, for at most `timeout` ms.
 * @param {Element | null} root
 * @param {number} [timeout]
 */
function imagesReady(root, timeout = 700) {
  const images = Array.from(root?.querySelectorAll('img') ?? []);
  images.forEach((img) => {
    if (img.loading === 'lazy') img.loading = 'eager';
  });
  const decoded = Promise.allSettled(images.map((img) => img.decode()));
  return Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, timeout))]);
}

/**
 * How long the closed drawer waits for its payment parts (assets/sl-cart-parts.js): at least this long after the new
 * markup goes in (on a fast connection they are drawn ~0.3-0.5s after it)...
 */
const PAYMENT_PARTS_WAIT = 500;
/** ...and, on a slow phone or connection, at most this long after the tap: the cart opening fast matters more than it opening complete (parts still drawing then fade in once drawn). Was 2.6s. */
const PAYMENT_PARTS_DEADLINE = 1300;

/**
 * Puts the drawer markup from the add response in place while the drawer is closed, then waits for its images and its
 * payment parts, so it opens complete.
 * @param {Record<string, string> | undefined} sections
 * @param {number} tappedAt - performance.now() of the tap
 * @returns {Promise<boolean>} Whether the drawer markup was rendered.
 */
async function renderDrawer(sections, tappedAt) {
  /** @type {any} */
  const items = cartDrawer()?.querySelector('cart-items-component');
  const html = items?.dataset.sectionId ? sections?.[items.dataset.sectionId] : undefined;
  if (!items || !html) return false;
  await customElements.whenDefined('cart-items-component');
  if (typeof items.renderSectionHtml !== 'function') return false;
  beforeCartChange(cartDrawer());
  await items.renderSectionHtml(html);
  const drawer = cartDrawer();
  if (drawer?.isOpen) {
    // Already on screen (desktop, beside the page): nothing to hold back. Its payment parts are hidden right away,
    // before their placeholders can show, and fade in together once drawn.
    revealWhenDrawn(drawingParts(drawer));
    return true;
  }
  const deadline = Math.max(performance.now() + PAYMENT_PARTS_WAIT, tappedAt + PAYMENT_PARTS_DEADLINE);
  const [, drawing] = await Promise.all([imagesReady(drawer), partsDrawn(drawer, deadline)]);
  revealWhenDrawn(drawing);
  return true;
}

/**
 * The drawer's banner (snippets/sl-cart-drawer-top.liquid, URL given by sections/sl-product.liquid), fetched while
 * the shopper reads the product page so it is already there when the drawer opens.
 * @type {HTMLImageElement | null}
 */
let warmBanner = null;
function warmDrawerImages() {
  if (warmBanner) return;
  const url = document.querySelector('[data-sl-cart-banner]')?.getAttribute('data-sl-cart-banner');
  if (!url) return;
  warmBanner = new Image();
  warmBanner.src = url;
  warmBanner.decode().catch(() => {});
}
/**
 * The express payment buttons' script, loaded through Shopify's own loader (it does nothing once loaded, and pages
 * whose cart already has items have called it). Loaded ahead, the buttons draw in ~0.2s after the first add instead
 * of ~0.8s on a first visit, so the drawer waits less for them.
 */
function warmPayments() {
  try {
    /** @type {any} */ (window).Shopify?.PaymentButton?.init?.();
  } catch {}
}

/**
 * The installments line's script. Shopify loads it only once the element shows up, i.e. with the first cart markup,
 * and it then takes ~0.15s (~1s on a slow phone) before the line can even start its own ~0.3s draw. Loaded from the
 * very URL Shopify uses (the page lists it in Shopify.featureAssets: same URL, same module, defined once), it is ready
 * when the markup goes in. Started on the tap, so shoppers who never add pay nothing for it.
 */
function warmPaymentTerms() {
  try {
    if (customElements.get('shopify-payment-terms')) return;
    const path = /** @type {any} */ (window).Shopify?.featureAssets?.['shop-js']?.['payment-terms']?.[0];
    const src = document.querySelector('script[src*="/shop-js/modules/"]')?.getAttribute('src');
    if (typeof path !== 'string' || !src) return;
    const base = new URL(src, location.href).href;
    import(base.slice(0, base.indexOf('/shop-js/') + '/shop-js/'.length) + path).catch(() => {});
  } catch {}
}

/**
 * @param {string} href
 * @param {string} rel
 * @param {string} [as]
 */
function hint(href, rel, as) {
  if (Array.from(document.head.querySelectorAll(`link[rel="${rel}"]`)).some((l) => l.getAttribute('href') === href)) return;
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (as) link.as = as;
  document.head.append(link);
}

/**
 * The payment buttons load their partners' scripts once the cart markup is in. Fetched from the tap instead, while
 * /cart/add.js is on its way: Google Pay's script (~70KB; ~0.6s on a first visit, the slowest part of the drawer) when
 * the shop offers Google Pay, and the connections PayPal draws its button from when it offers PayPal (data-sl-gpay /
 * data-sl-paypal, sections/sl-product.liquid). Shopify adds the same plain script tag afterwards and gets this copy.
 */
function warmWalletScripts() {
  const script = document.querySelector('script[data-sl-cart-banner]');
  if (script?.hasAttribute('data-sl-gpay')) hint('https://pay.google.com/gp/p/js/pay.js', 'preload', 'script');
  if (script?.hasAttribute('data-sl-paypal')) {
    hint('https://www.paypal.com', 'preconnect');
    hint('https://www.paypalobjects.com', 'preconnect');
  }
}

/** How long the product form (options + buy button) or the buy bar stays on screen before the drawer's banner and payment script are fetched ahead */
const WARM_DWELL = 1000;

function warmWhenIdle() {
  let warmed = false;
  const warm = () => {
    if (warmed) return;
    warmed = true;
    warmDrawerImages();
    warmPayments();
  };
  // Stillwell: the drawer banner and the wallet script (~145KB) are fetched ahead only when the shopper shows intent;
  // they downloaded after every product view, also for the many who never add. On slow or data-saving connections the
  // warm-up starts when a finger or the mouse goes down on an add button, a moment before the add itself.
  const c = /** @type {any} */ (navigator).connection;
  const slow = !!c && (c.saveData || /(^|-)2g|3g/.test(String(c.effectiveType || '')));
  const touched = slow ? '[data-sl-add]' : '[data-sl-add], .sl-pdp__opt';
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.target instanceof Element && event.target.closest(touched)) warm();
    },
    { capture: true, passive: true }
  );
  if (slow) return;
  const idle = /** @type {any} */ (window).requestIdleCallback;
  const soon = () => {
    if (typeof idle === 'function') idle(warm, { timeout: 4000 });
    else setTimeout(warm, 200);
  };
  // ...else once the product form (its option cards come before the buy button) or the buy bar (while it shows) has
  // stayed on screen for a moment
  const buttons = document.querySelectorAll('form[data-sl-form], [data-sl-buybar]');
  if (!buttons.length || typeof IntersectionObserver !== 'function') {
    soon();
    return;
  }
  /** @type {Set<Element>} */
  const seen = new Set();
  let timer = 0;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) seen.add(entry.target);
        else seen.delete(entry.target);
      }
      if (seen.size && !timer) {
        timer = window.setTimeout(() => {
          io.disconnect();
          soon();
        }, WARM_DWELL);
      } else if (!seen.size && timer) {
        clearTimeout(timer);
        timer = 0;
      }
    },
    { threshold: 0 }
  );
  buttons.forEach((button) => io.observe(button));
}
if (document.readyState === 'complete') warmWhenIdle();
else window.addEventListener('load', warmWhenIdle, { once: true });

/**
 * Shows the message next to the button that was used: the buy bar's own line when the add came from the bar (the
 * form's box sat ~1,400px above the screen then, and the shopper saw nothing happen), else the form's box.
 * @param {HTMLFormElement} form
 * @param {string} message
 * @param {Element | null} [submitter]
 */
function showError(form, message, submitter) {
  const barBox = submitter?.closest('[data-sl-buybar]')?.querySelector('[data-sl-bar-error]');
  const box = barBox ?? form.querySelector('[data-sl-cart-error]');
  if (!box) return;
  box.textContent = message;
  box.hidden = false;
}

/** Longer than the drawer's slide-in (--animation-speed, ~0.15s) */
const DRAWER_SLIDE = 300;

/** Guards both the main button and the sticky button against duplicate submissions. */
const pendingForms = new WeakSet();

/**
 * Applies a product offer to the already-added units and returns discounted cart sections.
 * This never adds merchandise: a failed discount request must not repeat the add request.
 * @param {string} code
 * @param {string[]} sectionIds
 */
async function applyOffer(code, sectionIds) {
  const existing = await fetchCart();
  const codes = new Set((existing.discount_codes ?? []).map((discount) => discount.code));
  codes.add(code);
  const response = await fetch(Theme.routes.cart_update_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({ discount: [...codes].join(','), sections: sectionIds, sections_url: location.pathname }),
    }),
  });
  if (!response.ok) throw new Error('Offer discount request failed');
  const cart = await response.json();
  const applied = cart.discount_codes?.some(
    (discount) => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable
  );
  if (!applied) throw new Error('Offer discount was not applied');
  return cart;
}

/** @param {SubmitEvent} event */
async function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-sl-form]')) return;
  const offerCode = form.dataset.slOfferCode?.trim();
  if (!cartDrawer() && !offerCode) return;

  event.preventDefault();
  if (pendingForms.has(form)) return;
  if (form.dataset.slOfferRecovery) {
    location.assign(form.dataset.slOfferRecovery);
    return;
  }
  pendingForms.add(form);
  const tappedAt = performance.now();
  // The closed drawer is laid out from now on (snippets/cart-drawer.liquid), so its parts draw before it opens
  /** @type {any} */ (window).slWarmDrawer?.();
  warmDrawerImages();
  warmPayments();
  warmPaymentTerms();
  warmWalletScripts();
  const buttons = document.querySelectorAll(`[data-sl-add]`);
  const busy = (/** @type {boolean} */ on) =>
    buttons.forEach((b) => (on ? b.setAttribute('aria-busy', 'true') : b.removeAttribute('aria-busy')));
  busy(true);

  const errorBox = form.querySelector('[data-sl-cart-error]');
  if (errorBox instanceof HTMLElement) errorBox.hidden = true;
  document.querySelectorAll('[data-sl-bar-error]').forEach((b) => {
    if (b instanceof HTMLElement) b.hidden = true;
  });
  const submitter = event.submitter instanceof Element ? event.submitter : null;

  const formData = new FormData(form);
  // return_to is reserved for native HTML fallback, not the AJAX endpoint.
  formData.delete('return_to');
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  // A multi-unit option ("Both knees") goes in as that many single units (data-add-id × data-add-qty, set by
  // sections/sl-product.liquid when Theme settings > "Both knees adds two single wraps" is on); an automatic discount
  // in Shopify admin prices the pair.
  const picked = form.querySelector('input[name="id"]:checked');
  if (picked instanceof HTMLInputElement && picked.dataset.addId) {
    formData.set('id', picked.dataset.addId);
    formData.set('quantity', picked.dataset.addQty || '2');
  }

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  let opened = false;
  let addAttempted = false;
  const hold = offerCode ? CartItemsComponent.hold() : null;
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    await hold?.ahead;
    const config = fetchConfig('javascript', { body: formData });
    addAttempted = true;
    const response = await fetch(Theme.routes.cart_add_url, { ...config, headers: { ...config.headers, Accept: 'text/html' } });
    const result = await response.json();

    if (result.status) {
      const cart = await fetchCart();
      form.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: { description: result.description } }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'sl-product' } });
      showError(form, result.description || result.message || 'Something went wrong. Please try again.', submitter);
      return;
    }

    if (offerCode) {
      const offerCart = await applyOffer(offerCode, sectionIds);
      // The final cart and section HTML must reflect the discount, never the intermediate full price.
      result.sections = offerCart.sections;
      const rendered = await renderDrawer(result.sections, tappedAt).catch(() => false);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(offerCart),
        detail: { didError: false, items: offerCart.items, sections: result.sections, source: 'sl-product', itemCount: quantity },
      });
      if (rendered) {
        await openDrawer();
        opened = true;
      } else {
        location.assign(Theme.routes.cart_url);
      }
      return;
    }

    // The cart (for the event) loads while the drawer markup goes in; the drawer opens as soon as that is ready.
    // The button keeps its spinner until the cart is confirmed and the drawer has slid over it (finally), so its
    // label never flashes back while the drawer is still sliding in.
    const cartRequest = fetchCart();
    const rendered = await renderDrawer(result.sections, tappedAt).catch(() => false);
    if (rendered) await openDrawer();
    opened = rendered;

    let cart;
    try {
      cart = await cartRequest;
    } catch (error) {
      // The line is already in the cart and on screen: settle the listeners without a false error message.
      if (rendered) return deferred.reject(error);
      throw error;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, sections: result.sections, source: 'sl-product', itemCount: quantity },
    });
    if (!rendered) {
      openDrawer();
      opened = true;
    }
  } catch (error) {
    deferred.reject(error);
    form.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    if (offerCode && addAttempted) {
      // Keep a retry from adding another pair. Shopify's discount route can recover server-side.
      const recovery = new URL(Theme.routes.cart_url, location.origin);
      recovery.pathname = recovery.pathname.replace(/\/cart\/?$/, `/discount/${encodeURIComponent(offerCode)}`);
      recovery.searchParams.set('redirect', Theme.routes.cart_url);
      form.dataset.slOfferRecovery = recovery.href;
      showError(form, form.dataset.slOfferError, submitter);
      location.assign(recovery.href);
    } else {
      showError(form, 'We could not reach the store. Check your connection and try again.', submitter);
    }
  } finally {
    hold?.release();
    const unlock = () => {
      pendingForms.delete(form);
      busy(false);
    };
    if (opened) setTimeout(unlock, DRAWER_SLIDE);
    else unlock();
  }
}

document.addEventListener('submit', onSubmit);
